import type {
  FormattedMemberTransaction,
  FormattedMemberTransactions,
  FormattedMemberTransactionsResponse,
  MemberTransactionsAPIRequest,
  MemberTransactionsAPIResponse
} from '@library/types/members/memberTransactions';
import { concat, mergeDeepWith } from 'ramda';
import {
  transformMemberTransactions,
  transformTransactionTypes
} from '@bo-utils/members/memberTransactions';
import type { ComboboxOption } from '@library/components/selects';
import type { GenericFilterTypesApiResponse } from '@library/types/utils';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';
import { defaultHeaders } from '@bo-constants/apiHeaders';

const { memberSupport, memberTransactions } = apiPaths;

export const memberTransactionsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getMemberTranactionTypes: build.query<Array<ComboboxOption>, void>({
      query: () => ({
        url: `${memberSupport}member_transactions/transaction_types`,
        method: 'GET',
        headers: defaultHeaders
      }),
      transformResponse: (
        response: GenericFilterTypesApiResponse | undefined
      ) => {
        return transformTransactionTypes({ response });
      }
    }),
    getMemberTransactions: build.query<
      FormattedMemberTransactionsResponse,
      MemberTransactionsAPIRequest
    >({
      query: ({ createdAt, memberId, page, perPage, transactionTypes }) => ({
        url: `${memberTransactions}/${memberId}/member_transactions`,
        method: 'GET',
        params: {
          created_at: createdAt,
          page,
          per_page: perPage,
          transaction_types: transactionTypes
        },
        headers: defaultHeaders
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { page, ...rest } = queryArgs;
        return `${endpointName}${Object.entries(rest).flat(1).join('')}`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1) {
          return newItems;
        }

        return mergeDeepWith(
          (a: FormattedMemberTransaction, b: FormattedMemberTransaction) => {
            if (Array.isArray(a) && Array.isArray(b)) {
              return concat(a, b) as FormattedMemberTransactions;
            }
            return b;
          },
          currentCache,
          newItems
        ) as FormattedMemberTransactionsResponse;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        JSON.stringify(currentArg) !== JSON.stringify(previousArg),
      transformResponse: (
        response: MemberTransactionsAPIResponse,
        _,
        { page, perPage }
      ) => {
        return transformMemberTransactions({ page, perPage, response });
      },
      providesTags: ['MemberTransactions']
    })
  })
});

export const {
  useGetMemberTransactionsQuery,
  useGetMemberTranactionTypesQuery
} = memberTransactionsApi;
